/**
 * Agent attribution hat shown above an assistant message body so per-turn
 * agent switching is visible to the user.
 */
import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import AvatarSkeleton from '@atlaskit/avatar/Skeleton';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { AgentProfileCardWithTrigger } from '@atlassian/agent-profile-card/card-with-trigger';
import { AvatarRelay } from '@atlassian/avatar-relay/avatar-relay';

import { DefaultRovoAvatar } from '../../../common/ui/default-rovo-avatar';

import { type agentResponseHatIdentityAccountQuery } from './__generated__/agentResponseHatIdentityAccountQuery.graphql';
import messages from './agent-response-hat-messages';

export type AgentResponseHatProps = {
	/** True when the current response author is the default Rovo assistant. */
	isDefaultRovo?: boolean;
	/** Identity account id (ARI) for specialist-agent response authors. Ignored for default Rovo. */
	agentIdentityAccountId?: string;
	/** Cloud/site id used by the agent profile card to fetch agent data. Enables the hover card. */
	cloudId?: string;
	/**
	 * True while a known specialist author's identity account id is still hydrating (e.g. an
	 * optimistic turn that is streaming before the backend author / agent lookup resolves).
	 * When set, the hat reserves space with a skeleton instead of rendering nothing, so it
	 * appears right after the thinking indicator hands off without flashing the default identity.
	 */
	isIdentityHydrating?: boolean;
	/** Optional test id for the outer wrapper. */
	testId?: string;
};

const AgentResponseHatFallback = (): React.JSX.Element => (
	<>
		<AvatarSkeleton appearance="hexagon" size="small" />
		<Skeleton width="72px" height="12px" borderRadius={token('radius.xsmall')} isShimmering />
	</>
);

const AgentResponseHatMetadata = ({
	identityAccount,
}: {
	identityAccount: {
		readonly name?: string | null;
		readonly ' $fragmentSpreads': import('relay-runtime').FragmentRefs<'avatarRelay_user'>;
	};
}): React.JSX.Element => {
	const relayName = identityAccount.name?.trim();

	return (
		<>
			<AvatarRelay userFragment={identityAccount} size="small" name={relayName} />
			{relayName ? (
				<Text size="small" weight="medium" color="color.text.subtle">
					{relayName}
				</Text>
			) : null}
		</>
	);
};

const AgentResponseHatMetadataFromIdentityAccount = ({
	identityAccountId,
}: {
	identityAccountId: string;
}): React.JSX.Element | null => {
	const data = useLazyLoadQuery<agentResponseHatIdentityAccountQuery>(
		graphql`
			query agentResponseHatIdentityAccountQuery($identityAccountId: ID!) {
				user(accountId: $identityAccountId) {
					name
					# AvatarRelay owns this fragment; this query only passes the fragment ref through.
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...avatarRelay_user
				}
			}
		`,
		{ identityAccountId },
		{ fetchPolicy: 'store-or-network' },
	);

	return data.user ? <AgentResponseHatMetadata identityAccount={data.user} /> : null;
};

const getAccountIdFromIdentityAccountId = (
	value: string | null | undefined,
): string | undefined => {
	const trimmedValue = value?.trim();
	if (!trimmedValue) {
		return undefined;
	}

	const identityUserAriPrefix = 'ari:cloud:identity::user/';
	if (trimmedValue.startsWith(identityUserAriPrefix)) {
		return trimmedValue.slice(identityUserAriPrefix.length);
	}

	return trimmedValue.startsWith('ari:') ? undefined : trimmedValue;
};

export const AgentResponseHat = ({
	isDefaultRovo,
	agentIdentityAccountId,
	isIdentityHydrating,
	cloudId,
	testId,
}: AgentResponseHatProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const resolvedIsDefaultRovo = isDefaultRovo ?? !agentIdentityAccountId;
	const agentAccountId = getAccountIdFromIdentityAccountId(agentIdentityAccountId);

	if (isIdentityHydrating && !resolvedIsDefaultRovo) {
		return (
			<Inline space="space.075" alignBlock="center" testId={testId}>
				<AgentResponseHatFallback />
			</Inline>
		);
	}

	if (!resolvedIsDefaultRovo) {
		if (!agentAccountId) {
			return null;
		}

		const hatContent = (
			<Inline space="space.075" alignBlock="center" testId={testId}>
				<React.Suspense fallback={<AgentResponseHatFallback />}>
					<AgentResponseHatMetadataFromIdentityAccount identityAccountId={agentAccountId} />
				</React.Suspense>
			</Inline>
		);

		if (cloudId && agentIdentityAccountId && isExperimentEnabled('rovo_chat_mention_agents')) {
			return (
				<AgentProfileCardWithTrigger
					trigger="hover"
					identityAccountId={agentIdentityAccountId}
					cloudId={cloudId}
					hideConversationStarters
				>
					{hatContent}
				</AgentProfileCardWithTrigger>
			);
		}

		return hatContent;
	}

	return (
		<Inline space="space.075" alignBlock="center" testId={testId}>
			<DefaultRovoAvatar size="small" />
			<Text size="small" weight="medium" color="color.text.subtle">
				{formatMessage(messages.defaultRovoName)}
			</Text>
		</Inline>
	);
};
